/*
<purpose>
  Compare mode (2026-10-30): the stage where the mail and the letter stand side by side, each in its
  own scroll. It replaces the KI-Vorschlag maximize: inline it is a transparent wrapper around the
  letter; open, the SAME letter subtree flips to the right column of a fixed overlay above a dimmed
  backdrop, the mail column joins on its left, the ticket header sits above and the footer (letter
  check + buttons) is pinned below. Marks live only here: one per column, on the sentence the clerk
  pointed at; the column glides 280 ms so the sentence sits 30 % down, then the mark fades in-out-in.
</purpose>
<dependencies>theme; services/textRange (finding a quote without touching the DOM); portal to
document.body (backdrop only).</dependencies>
<constraints>
  - NEVER portal or remount the letter: unconfirmed fill-in input and the edit buffer live in
    component state and die on remount. The flip is style-only; the mail column is a sibling that
    mounts BEFORE the letter column in a stable child slot.
  - Layout Stability: a measured placeholder holds the inline height while the subtree is fixed.
  - A mark is a LAYER drawn from the quote's client rects inside the column's own scroll; the
    content is never wrapped or mutated (the letter column is React-owned).
  - z-index 900 / backdrop 899: modals and toasts (1000+) stack above; the top inset clears the
    sticky NavBar unless the host has none (`topInset`).
</constraints>
*/
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '../theme';
import { findTextRange } from '../services/textRange';

export interface CompareMarks {
  mail: string | null;
  letter: string | null;
  /** Bumps on every pick, so picking the same pair again re-runs the glide and the mark. */
  seq: number;
}

interface CompareStageProps {
  open: boolean;
  onClose: () => void;
  /** The overlay's accessible name and its top line. */
  title: string;
  /** The ticket header, drawn above the columns while open. */
  header?: React.ReactNode;
  /** The mail column's content (open only). */
  mail?: React.ReactNode;
  /** Above the letter in both states (inline: its title and the door; open: its title/switch). */
  letterHead?: React.ReactNode;
  /** A row without a letter: the stage opens on the mail alone. */
  letterless?: boolean;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  banner?: React.ReactNode;
  topInset?: number;
  marks?: CompareMarks | null;
}

export const COMPARE_STAGE_INSET = theme.spacing.lg * 2;
const OVERLAY_INSET = COMPARE_STAGE_INSET;
const OVERLAY_TOP = theme.layout.navBarHeight + OVERLAY_INSET / 2;
export const MARK_COLOR = '#ffe38a';
const GLIDE_MS = 280;

const reducedMotion = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Scroll `col` so `top` (content px) sits 30 % down; resolves when it is there. */
const glide = (col: HTMLElement, rectTop: number, rectBottom: number, done: () => void): (() => void) => {
  const view = col.clientHeight;
  const inView = rectTop >= col.scrollTop + 8 && rectBottom <= col.scrollTop + view - 8;
  const to = Math.max(0, Math.min(rectTop - view * 0.3, col.scrollHeight - view));
  const from = col.scrollTop;
  if (inView || Math.abs(to - from) < 2 || reducedMotion() || typeof requestAnimationFrame !== 'function') {
    if (!inView) col.scrollTop = to;
    done();
    return () => {};
  }
  let cancelled = false;
  const t0 = performance.now();
  const ease = (t: number) => 1 - Math.pow(1 - t, 4);
  const step = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - t0) / GLIDE_MS);
    col.scrollTop = from + (to - from) * ease(t);
    if (t < 1) requestAnimationFrame(step); else done();
  };
  requestAnimationFrame(step);
  return () => { cancelled = true; };
};

/** The mark of one column: found in the column's content, drawn as a layer, glided into view. */
const MarkLayer: React.FC<{ scrollRef: React.RefObject<HTMLDivElement | null>; quote: string | null; seq: number; col: 'mail' | 'letter' }> = ({ scrollRef, quote, seq, col }) => {
  const [mark, setMark] = useState<{ seq: number; rects: { top: number; left: number; width: number; height: number }[] } | null>(null);
  // A passive effect, not a layout one: the column's ref (this layer's parent) is attached only
  // after child layout effects have run.
  useEffect(() => {
    setMark(null);
    const scroller = scrollRef.current;
    if (!scroller || !quote) return undefined;
    const range = findTextRange(scroller, quote);
    if (!range) return undefined;
    const box = scroller.getBoundingClientRect();
    const raw = typeof range.getClientRects === 'function' ? Array.from(range.getClientRects()) : [];
    const rects = raw.filter(r => r.width > 0).map(r => ({
      top: r.top - box.top + scroller.scrollTop, left: r.left - box.left + scroller.scrollLeft, width: r.width, height: r.height,
    }));
    const top = rects.length ? Math.min(...rects.map(r => r.top)) : 0;
    const bottom = rects.length ? Math.max(...rects.map(r => r.top + r.height)) : 0;
    return glide(scroller, top, bottom, () => setMark({ seq, rects }));
  }, [scrollRef, quote, seq]);
  if (!mark) return null;
  return (
    <div data-mark-layer="" aria-hidden="true" data-testid={`compare-mark-${col}`} data-quote={quote ?? ''}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {mark.rects.map((r, i) => (
        <span key={`${mark.seq}-${i}`} style={{
          position: 'absolute', top: r.top - 1, left: r.left - 2, width: r.width + 4, height: r.height + 2,
          backgroundColor: MARK_COLOR, mixBlendMode: 'multiply', borderRadius: 2,
          animation: 'stx-markInOutIn 1.6s ease-in-out forwards',
        }} />
      ))}
    </div>
  );
};

const columnHeadStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: theme.colors.textSecondary, padding: '0 0 6px', flexShrink: 0,
};

const CompareStage: React.FC<CompareStageProps> = ({
  open, onClose, title, header, mail, letterHead, letterless, children, footer, banner, topInset, marks,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const mailScrollRef = useRef<HTMLDivElement>(null);
  const letterScrollRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const lastInlineHeight = useRef(0);
  useLayoutEffect(() => {
    if (!open && wrapperRef.current) lastInlineHeight.current = wrapperRef.current.offsetHeight;
  });

  // Where focus was before the stage opened — read before any child takes focus on mount.
  useLayoutEffect(() => {
    if (open) restoreFocusRef.current = document.activeElement as HTMLElement | null;
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    // A door that already placed focus inside the stage (the count segment's first chip) keeps it.
    if (!wrapperRef.current?.contains(document.activeElement)) closeRef.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreFocusRef.current?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);

  return (
    <>
      {open && (
        <div data-testid="compare-stage-placeholder" aria-hidden="true" style={{ height: lastInlineHeight.current, flexShrink: 0 }} />
      )}
      {open && createPortal(
        <div
          data-testid="compare-stage-backdrop"
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 899, backgroundColor: 'rgba(10, 18, 83, 0.38)', animation: 'stx-fadeIn 0.28s ease-out' }}
        />,
        document.body,
      )}
      <div
        ref={wrapperRef}
        tabIndex={-1}
        data-testid="compare-stage"
        {...(open ? { role: 'dialog', 'aria-modal': true, 'aria-label': title } : {})}
        style={open ? {
          position: 'fixed', zIndex: 900,
          top: topInset ?? OVERLAY_TOP, right: OVERLAY_INSET, bottom: OVERLAY_INSET, left: OVERLAY_INSET,
          // Inset on all four sides of the viewport; border-box, so its own border and the footer's
          // padding stay inside what the viewport shows.
          boxSizing: 'border-box', maxHeight: `calc(100vh - ${(topInset ?? OVERLAY_TOP) + OVERLAY_INSET}px)`,
          backgroundColor: theme.colors.white, borderRadius: 10, boxShadow: '0 10px 30px rgba(10,15,50,0.25)',
          border: `1px solid ${theme.colors.borderLight}`,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px ${theme.spacing.md}px`,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, outline: 'none',
          animation: 'stx-fadeIn 0.28s cubic-bezier(.2,0,0,1)',
        } : { outline: 'none' }}
      >
        {open && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexShrink: 0,
            paddingBottom: theme.spacing.sm, marginBottom: theme.spacing.sm, borderBottom: `1px solid ${theme.colors.borderLight}`,
          }}>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: theme.colors.navy }}>{title}</span>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              title="Zurück (Esc)"
              data-testid="compare-close"
              style={{
                fontFamily: theme.font.family, fontSize: 11.5, fontWeight: 600, color: theme.colors.navyMid,
                backgroundColor: theme.colors.white, border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: 5, padding: '1px 9px', cursor: 'pointer', lineHeight: '18px',
              }}
            >Zurück</button>
          </div>
        )}
        {open && banner && <div data-testid="compare-stage-banner" style={{ flexShrink: 0 }}>{banner}</div>}
        {open && header && <div style={{ flexShrink: 0 }}>{header}</div>}
        {/* The columns. Inline this is an inert block around the letter; the mail column occupies a
            stable child slot BEFORE the letter column, so opening never remounts the letter. */}
        <div style={open ? {
          flex: 1, minHeight: 0, display: 'grid', gap: theme.spacing.lg,
          gridTemplateColumns: letterless ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1fr)',
        } : undefined}>
          {open ? (
            <div data-testid="compare-col-mail" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={columnHeadStyle}>E-Mail</div>
              <div ref={mailScrollRef} data-col="mail" style={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {mail}
                <MarkLayer scrollRef={mailScrollRef} quote={marks?.mail ?? null} seq={marks?.seq ?? 0} col="mail" />
              </div>
            </div>
          ) : null}
          <div data-testid="compare-col-letter" hidden={open && letterless} style={open ? { display: letterless ? 'none' : 'flex', flexDirection: 'column', minHeight: 0 } : undefined}>
            {letterHead}
            <div
              ref={letterScrollRef}
              data-testid="compare-stage-scroll"
              data-col="letter"
              style={open ? { position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto', overflowAnchor: 'none' } : undefined}
            >
              {children}
              {open ? <MarkLayer scrollRef={letterScrollRef} quote={marks?.letter ?? null} seq={marks?.seq ?? 0} col="letter" /> : null}
            </div>
          </div>
        </div>
        {open && footer && <div data-testid="compare-stage-footer" style={{ flexShrink: 0 }}>{footer}</div>}
      </div>
    </>
  );
};

export default CompareStage;
